import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { useDeliveryFilterStore } from '@/app/store/deliveryFilterStore';
import { useNavigate, useLocation } from 'react-router-dom';
import { SyncStatus } from '@/app/components/SyncStatus';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { fetchDeliveryStatusCounts } from '@/app/services/deliveryStatusService';
import { fetchDraftOrderCountBySource } from '@/app/services/clientOrderService';
import { StatusRail, StatusRailItem } from '@/app/components/StatusRail';
import { useCorporateSalesGroupId } from '@/app/hooks/useCorporateSalesGroupId';
import { CommandCenter } from '@/app/components/CommandCenter';
import { getDisplayKey, globalHotkeyDefinitions, hotkeyOptions } from '@/app/config/hotkeys';
import { useHotkeys } from 'react-hotkeys-hook';

interface HeaderProps {
    isMobile?: boolean;
}

/**
 * Slim app bar: sidebar toggle, global search, and the live work counters.
 * Account actions live in the sidebar footer (see NavUser).
 */
const Header: React.FC<HeaderProps> = ({ isMobile }) => {
    const { setPendingFilter } = useDeliveryFilterStore();
    const navigate = useNavigate();
    const location = useLocation();
    const [isCommandCenterOpen, setIsCommandCenterOpen] = useState(false);

    useHotkeys(
        globalHotkeyDefinitions.commandCenter.keys,
        (e) => {
            e.preventDefault();
            setIsCommandCenterOpen((isOpen) => !isOpen);
        },
        { ...hotkeyOptions, enableOnFormTags: ['INPUT', 'TEXTAREA', 'SELECT'] }
    );

    // CORPORATE SALES invoices are intentionally left un-delivered, so exclude
    // them from the delivery-status badges (counts + the list they open).
    const { groupId: corporateGroupId, isResolved: isCorporateGroupResolved } =
        useCorporateSalesGroupId();

    // Fetch delivery status counts with 10-minute refresh interval.
    // Held until the corporate group resolves: the id is part of the key AND of
    // the request, so running early costs a full duplicate round of 4 calls.
    const { data: deliveryStatusCounts, isLoading: isLoadingCounts } = useQuery({
        queryKey: ['deliveryStatusCounts', corporateGroupId],
        queryFn: () => fetchDeliveryStatusCounts(corporateGroupId),
        enabled: isCorporateGroupResolved,
        refetchInterval: 600000, // Refresh every 10 minutes (10 * 60 * 1000)
        staleTime: 540000, // Consider data stale after 9 minutes (9 * 60 * 1000)
    });

    // Draft sales orders per app channel (not yet converted to invoices):
    // customer PWA and sales-manager PWA each get their own counter.
    const { data: clientDraftCount, isLoading: isLoadingClientDrafts } = useQuery({
        queryKey: ['draftOrderCount', 'client_app'],
        queryFn: () => fetchDraftOrderCountBySource('client_app'),
        refetchInterval: 600000, // Refresh every 10 minutes
        staleTime: 540000, // Consider data stale after 9 minutes
    });
    const { data: managerDraftCount, isLoading: isLoadingManagerDrafts } = useQuery({
        queryKey: ['draftOrderCount', 'manager_app'],
        queryFn: () => fetchDraftOrderCountBySource('manager_app'),
        refetchInterval: 600000, // Refresh every 10 minutes
        staleTime: 540000, // Consider data stale after 9 minutes
    });

    // Navigation handlers for delivery status badges
    const handleDeliveryStatusClick = (status: 'not_delivered' | 'packed' | 'shipped' | 'partially_delivered') => {
        // Set the pending filter in Zustand store
        // The sales invoice list page will listen to this and update its nuqs state
        setPendingFilter(status);

        // Navigate to sales invoices list page
        // If already there, the route won't change but the useEffect will trigger from store change
        if (location.pathname !== '/sales-invoices/list') {
            navigate(`/sales-invoices/list?delivery_progress=${status}&is_cancelled=false&exclude_corporate=true`);
        }
    };

    // Navigate to the sales-orders list filtered to one app channel's draft
    // orders. String values match the Source/Status dropdown option values so
    // the selects display correctly.
    const handleDraftSourceClick = (source: 'client_app' | 'manager_app') => {
        const filters = encodeURIComponent(JSON.stringify({ order_source: source, status: 'draft' }));
        navigate(`/sales-orders/list?filters=${filters}`);
    };

    return (
        <>
            <header className={cn(
                "sticky top-0 z-30 shrink-0 border-b border-border/70 bg-card/90 backdrop-blur-md",
                "px-3 py-2 sm:px-4 sm:py-2.5"
            )}>
                <div className="flex items-center gap-2">
                    <SidebarTrigger className="h-8 w-8 shrink-0 text-muted-foreground" />
                    <Separator orientation="vertical" className="mr-1 hidden h-6 sm:block" />

                    <button
                        type="button"
                        onClick={() => setIsCommandCenterOpen(true)}
                        className={cn(
                            "relative flex w-full min-w-0 items-center gap-2 rounded-lg border border-border/80 bg-card px-2.5 py-2 shadow-2xs",
                            "text-sm text-muted-foreground hover:border-border hover:bg-accent/30 transition-colors duration-fast",
                            "sm:w-[280px] md:w-[340px] sm:shrink-0"
                        )}
                    >
                        <Search className="h-4 w-4 shrink-0" />
                        <span className="flex-1 truncate text-left">
                            {isMobile ? "Search..." : "Search invoices, parties, items..."}
                        </span>
                        <kbd className="hidden sm:inline-block rounded border border-border/70 bg-muted px-1.5 py-0.5 text-[10px] font-medium">
                            {getDisplayKey(globalHotkeyDefinitions.commandCenter.keys)}
                        </kbd>
                    </button>

                    {/* Work counters sit with the search box, not off at the far
                        right — they read as one toolbar. Sync alone is pushed
                        right. Two rails: delivery pipeline, then draft SOs by
                        app channel. */}
                    <div className="hidden items-center gap-2 md:flex">
                        <StatusRail>
                            <StatusRailItem
                                label="Not Delivered"
                                count={deliveryStatusCounts?.notDelivered || 0}
                                tone="destructive"
                                onClick={() => handleDeliveryStatusClick('not_delivered')}
                                isLoading={isLoadingCounts || !isCorporateGroupResolved}
                            />
                            <StatusRailItem
                                label="Packed"
                                count={deliveryStatusCounts?.packed || 0}
                                tone="neutral"
                                onClick={() => handleDeliveryStatusClick('packed')}
                                isLoading={isLoadingCounts || !isCorporateGroupResolved}
                            />
                            <StatusRailItem
                                label="Shipped"
                                count={deliveryStatusCounts?.shipped || 0}
                                tone="info"
                                onClick={() => handleDeliveryStatusClick('shipped')}
                                isLoading={isLoadingCounts || !isCorporateGroupResolved}
                            />
                            <StatusRailItem
                                label="Partial"
                                count={deliveryStatusCounts?.partiallyDelivered || 0}
                                tone="warning"
                                onClick={() => handleDeliveryStatusClick('partially_delivered')}
                                isLoading={isLoadingCounts || !isCorporateGroupResolved}
                            />
                        </StatusRail>

                        <StatusRail label="App SO">
                            <StatusRailItem
                                label="Client"
                                count={clientDraftCount || 0}
                                tone="info"
                                onClick={() => handleDraftSourceClick('client_app')}
                                isLoading={isLoadingClientDrafts}
                            />
                            <StatusRailItem
                                label="Sales Mgr"
                                count={managerDraftCount || 0}
                                tone="success"
                                onClick={() => handleDraftSourceClick('manager_app')}
                                isLoading={isLoadingManagerDrafts}
                            />
                        </StatusRail>
                    </div>

                    <div className="ml-auto hidden shrink-0 items-center sm:flex">
                        <SyncStatus />
                    </div>
                </div>
            </header>

            {/* Command Center (global search) */}
            <CommandCenter
                open={isCommandCenterOpen}
                onOpenChange={setIsCommandCenterOpen}
            />
        </>
    );
};

export default Header;
